import { useEffect, useMemo } from 'react';
import { getHpoolInfo } from '../services/createHPoolService';
import { getNumberOfSubscribers } from '../services/hpoolService';
import { getTikcetsInfo } from '../services/nftService';
import { WeiToEth } from '../services/web3Service';
import { formatDuration, processTimeLeft } from '../utils/date';
import { useEthConverter } from './useConverter';
import { usePromise } from './usePromise/usePromise';
import { useTimer } from './useTimer/useTimer';
import configJson from '../config/config.json';
import { useIsSubscribed } from './useIsSubscribed';
import { divideOnWei } from '../utils/mapper';
import { useUserStakes } from './useUserStakes';
import { useUserInfo } from './useUserInfo';
import { useStatusTime } from './poolInfo/useStatusTime';
import { getStatusPercentage } from '../components/Pages/Champion/ChampionChecker/ChampionOwner/statusMapper';
import { displaySmallFLoats } from '../components/Common/SliderTokensList/SliderToken/TokenPrice/TokenPrice';
import { useVpoolInfo } from './useVpoolInfo/useVpoolInfo';

export const formatNumber = (number = 0) => Number(number).toLocaleString();

export const WrapWithCoin = (value = 0, coin = '$') => `${coin}${value}`;

export const usePoolInfo = ({
  webHpoolId,
  nftWebToken,
  parentLoading,
  status,
  contractAddress,
  isVpool,
  poolChain = window.chainId,
}) => {
  const [getHpoolInfoReq, hpoolInfoRes, infoLoading, err] = usePromise(getHpoolInfo);
  const vpoolInfo = useVpoolInfo(contractAddress, webHpoolId);
  const { UserCanClaim, TokensInWallet } = useUserInfo(contractAddress);
  const userTotalPoolTokens = +TokensInWallet + +UserCanClaim || 0;

  const [_getNumberOfSubscribers, numberOfSubscribers] = usePromise(getNumberOfSubscribers, {
    defaultRes: 0,
  });
  const [getTicketsReserved, [ticketsReservedFrom = 0, ticketsReservedTo = 0], ticketsLoading] = usePromise(
    getTikcetsInfo,
    {
      defaultRes: [],
    },
  );
  const [, { totalHordStaked, numberOfAllTicketsReserved: ownedTickets }, _ticketsInfoLoading] =
    useUserStakes(nftWebToken);

  const [, , , , mergedConverter] = useEthConverter();
  const coinConverter = mergedConverter?.[configJson.baseCoin.toLowerCase()] || [];
  const [coinToUsd, usdToCoin] = coinConverter;

  const [isSubscribed, subscriptionAmountInCoin = 0, , , refreshSubscribtionInfo] = useIsSubscribed(webHpoolId);
  const subscriptionAmountInUsd = coinToUsd(subscriptionAmountInCoin);

  const hpoolRawInfo = useMemo(() => (typeof hpoolInfoRes === 'object' ? hpoolInfoRes : {}), [hpoolInfoRes]);

  // capacity foramatting process
  const champInitDepo = displaySmallFLoats(divideOnWei(hpoolRawInfo?.[1] || 0), 3);
  const rawTotalCapacity = hpoolRawInfo?.[6] || 0;
  const weiTotalCapacity = +WeiToEth(rawTotalCapacity) || 0;
  const capacityInUsd = +coinToUsd(weiTotalCapacity) || 0;

  const championAddress = isVpool ? hpoolRawInfo?.[1] : hpoolRawInfo?.[2] || '';

  const hpoolAddress = hpoolRawInfo?.[7];
  const formattedCapacity = formatNumber(capacityInUsd);

  const TIME_STATUS_MAPPER = {
    PENDING_INIT: 4,
    WHITELIST: 5,
    SUBSCRIPTION_PHASE: 6,
    TICKET_SALE: 9,
    PRIVATE_SUBSCRIPTION: 10,
    PUBLIC_SUBSCRIPTION: 11,
  };

  const timerBaseOnStatus = TIME_STATUS_MAPPER?.[status];
  const rawTimeLeftInSec = +hpoolRawInfo?.[timerBaseOnStatus] || 0;

  const standardTimeLeft = useStatusTime(status, isVpool);
  const timeLeftInSec = useTimer(processTimeLeft(rawTimeLeftInSec));

  const timeLeftPercentage = Math.floor((timeLeftInSec / standardTimeLeft) * 100);
  const validatedTimeLeftPercentage =
    timeLeftPercentage > 100 || timeLeftPercentage < 0 ? getStatusPercentage(status) : timeLeftPercentage;

  const formattedTimeLeft = useMemo(() => formatDuration(timeLeftInSec), [timeLeftInSec]);

  const allLoadings = [ticketsLoading, infoLoading, parentLoading].some((loading) => loading);

  const getInfo = () => {
    _getNumberOfSubscribers(webHpoolId, poolChain);
    getHpoolInfoReq(webHpoolId, isVpool, poolChain);
  };

  const getTicketInfo = () => {
    getTicketsReserved(nftWebToken);
  };
  const refreshData = () => {
    getInfo();
    getTicketInfo();
    refreshSubscribtionInfo();
  };

  useEffect(() => {
    if (!Number.isInteger(webHpoolId)) return;
    getInfo();
  }, [webHpoolId]);

  useEffect(() => {
    if (!Number.isInteger(nftWebToken) || ticketsReservedFrom || ticketsReservedTo) return;
    getTicketInfo();
  }, [nftWebToken]);

  return {
    numberOfSubscribers,
    ticketsReservedFrom,
    ticketsReservedTo,
    capacityInUsd,
    formattedCapacity,
    formattedTimeLeft,
    timeLeftPercentage: validatedTimeLeftPercentage,
    weiTotalCapacity,
    hpoolAddress,
    loading: allLoadings,
    championAddress,
    refreshData,
    champInitDepo,
    ownedTickets,
    totalHordStaked,
    infoIsFetched: !!hpoolInfoRes,
    subscriptionAmount: subscriptionAmountInCoin && { subscriptionAmountInCoin, subscriptionAmountInUsd },
    isSubscribed,
    userTotalPoolTokens,
    vpoolInfo,
  };
};
